import React, { useState, useEffect, useRef } from "react";
import ContentList from "./ContentList";
import SubscriptionList from "./SubscriptionList";
import SearchFilter from "./SearchFilter";
import "./purchase.css";
import { useSsoCart } from "../../../contexts/SsoCartContext";
import { useHistory } from "react-router-dom";
import PurchaseFilter from "./PurchaseFilter";
import SSOApiService from "../../../services/SsoServices";
import { SSO_FILTERS } from "../../../constants/api-constants";
import { useMasterData } from "../../../hooks/useMasterData";
import { Tabs, Tab } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PurchaseAddInsList from "./SubscriberAddins/PurchaseAddInsList";

const FILTERS_STORAGE_KEY = "purchase_filters";
const ACTIVE_TAB_STORAGE_KEY = "purchase_active_tab";

/*istanbul ignore next */
const PurchasePage = () => {
  const [searchText, setSearchText] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(() => {
    let initial = {};
    try {
      const ssoLoginData = localStorage.getItem("ssoLoginData");
      const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (ssoLoginData && savedFilters) {
        initial = JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error("Failed to load filters from localStorage:", error);
    }
    /* If the user landed here via an SSO deep link, pre-select the
     * distributorId from the SSO metadata so the Filter modal shows it
     * checked in the Distributor tab. */
    try {
      const raw = localStorage.getItem("ssoResponse");
      const meta = raw ? JSON.parse(raw)?.result?.metadata : null;
      const distId = Number(meta?.distributorId);
      const linkedContentIds =
        String(meta?.contentRedirectType) === "2"
          ? JSON.parse(meta?.linkedContentIds || "[]")
              .map(Number)
              .filter(Boolean)
          : [];
      if (distId) {
        const existing = Array.isArray(initial.distributors)
          ? initial.distributors
          : [];
        if (!existing.includes(distId)) {
          initial = { ...initial, distributors: [...existing, distId] };
        }
        initial.userIds = [distId];
      }
      if (linkedContentIds.length > 0) {
        initial.selectedIds = linkedContentIds;
      }
    } catch (error) {
      console.error(
        "Failed to parse SSO metadata for pre-selecting distributor filter:",
        error,
      );
    }
    return initial;
  });
  const [allStates, setAllStates] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
      return savedTab !== null ? Number.parseInt(savedTab, 10) : 0;
    } catch (error) {
      console.error("Failed to load active tab from localStorage:", error);
      return 0;
    }
  });
  const [isViewingSubscriptionDetail, setIsViewingSubscriptionDetail] =
    useState(() => {
      try {
        const savedSubscription = localStorage.getItem("selected_subscription");
        return savedSubscription !== null;
      } catch (error) {
        console.error(
          "Failed to load selected subscription from localStorage:",
          error,
        );
        return false;
      }
    });
  const { count, getCartDetails } = useSsoCart();
  const history = useHistory();
  const masterDataContext = useMasterData();
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("purchase-page-body");
    loadFilterOptions();
    // Load cart details to update cart  count
    if (getCartDetails) {
      getCartDetails();
    }
    return () => {
      document.body.classList.remove("purchase-page-body");
    };
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(selectedFilters),
      );
    } catch (error) {
      console.error("Failed to save filters to localStorage:", error);
    }
  }, [selectedFilters]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab.toString());
    } catch (error) {
      console.error("Failed to save active tab to localStorage:", error);
    }
  }, [activeTab]);

  const createSortedOptions = (items, mapper) => {
    return items.map(mapper).sort((a, b) => a.label.localeCompare(b.label));
  };

  const addFilterOption = (options, data, field, label, mapper) => {
    if (data && data.length > 0) {
      options.push({
        field,
        label,
        options: createSortedOptions(data, mapper),
      });
    }
  };

  const buildFilterOptions = (result, masterData) => {
    const options = [];

    if (masterData.states && masterData.states.length > 0) {
      setAllStates(masterData.states);
    }

    addFilterOption(
      options,
      result.contentOriginators,
      "contentOriginators",
      "Content Originator",
      (item) => ({ value: item, label: item }),
    );

    options.push({
      field: "contentType",
      label: "Content Type",
      options: [
        { value: 1, label: "Free" },
        { value: 2, label: "Paid" },
      ],
    });

    addFilterOption(
      options,
      masterData.countries,
      "countries",
      "Country",
      (item) => ({ value: item.countryId, label: item.name }),
    );

    options.push({
      field: "states",
      label: "State/Province",
      options: [],
    });

    addFilterOption(
      options,
      result.distributors,
      "distributors",
      "Distributor",
      (item) => ({
        value: item.userId,
        label: item.brandName || item.name || `User ${item.userId}`,
      }),
    );

    options.push({
      field: "contentFillable",
      label: "Fillable",
      options: [
        { value: 2, label: "Fillable" },
        { value: 3, label: "Fillable-Mapped" },
        { value: 1, label: "Non-Fillable" },
      ],
    });

    addFilterOption(
      options,
      masterData.lineOfBusiness,
      "lineOfBusiness",
      "Line of Business",
      (item) => ({ value: item.lineOfBusinessId, label: item.name }),
    );

    options.push({
      field: "typeOfContent",
      label: "Type of Content",
      options: [
        { value: 1, label: "PDF" },
        { value: 2, label: "FORMS_XFDL" },
        { value: 3, label: "JPG" },
        { value: 4, label: "WORD" },
        { value: 5, label: "EXCEL" },
        { value: 6, label: "PPT" },
        { value: 7, label: "AUDIO" },
        { value: 8, label: "GIF" },
        { value: 9, label: "PNG" },
        { value: 10, label: "VIDEO" },
        { value: 98, label: "CATALOG" },
        { value: 99, label: "BUNDLE" },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    });

    addFilterOption(
      options,
      masterData.typeOfForm,
      "typeOfForm",
      "Type of Form",
      (item) => ({ value: item.typeOfFormId, label: item.name }),
    );

    return options;
  };

  const loadFilterOptions = async () => {
    try {
      const response = await SSOApiService.ssoGet(SSO_FILTERS);
      const masterData = masterDataContext.masterDataValue;

      if (response?.result && masterData?.result) {
        const transformedOptions = buildFilterOptions(
          response.result,
          masterData.result,
        );
        setFilterOptions(transformedOptions);
      }
    } catch (error) {
      console.error("Failed to load filter options:", error);
    }
  };

  // Restore state dropdown options when filters are loaded from localStorage
  useEffect(() => {
    if (
      filterOptions.length > 0 &&
      allStates.length > 0 &&
      selectedFilters.countries &&
      selectedFilters.countries.length > 0
    ) {
      const filteredStates = allStates
        .filter((state) => selectedFilters.countries.includes(state.countryId))
        .map((item) => ({
          value: item.stateId,
          label: item.name,
          countryId: item.countryId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setFilterOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.field === "states"
            ? { ...option, options: filteredStates }
            : option,
        ),
      );
    }
  }, [filterOptions.length, allStates.length]);

  const onSearchHandler = (event) => {
    const value = event.target.value;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      // Only search if value is empty (clear search) or has at least 3 characters
      if (value.length === 0 || value.length >= 3) {
        setSearchText(value);
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleFilterClick = () => {
    setFilterOpen(true);
  };
  const handleClose = () => {
    setFilterOpen(false);
  };

  const handleFilterChange = (updatedFilters) => {
    // Update state options based on selected countries
    if (updatedFilters.countries && updatedFilters.countries.length > 0) {
      const filteredStates = allStates
        .filter((state) => updatedFilters.countries.includes(state.countryId))
        .map((item) => ({
          value: item.stateId,
          label: item.name,
          countryId: item.countryId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setFilterOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.field === "states"
            ? { ...option, options: filteredStates }
            : option,
        ),
      );
    } else {
      // Clear state options if no country is selected
      setFilterOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.field === "states" ? { ...option, options: [] } : option,
        ),
      );
    }
  };

  const applyFilterHandler = (filters) => {
    setSelectedFilters(filters);
    handleClose();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <div className="purchase-wrapper">
      <div className="header-with-tabs">
        <div className="header">
          <h2 className="purchase-title">Content</h2>
          {!isViewingSubscriptionDetail && (
            <button
              className="cart-header-icon"
              onClick={() => history.push("purchase/cart")}
              style={{ cursor: "pointer" }}
              aria-label="View cart"
            >
              <ShoppingCartOutlinedIcon />{" "}
              {count > 0 && <span className="cart-badge">{count}</span>}
            </button>
          )}
        </div>

        {!isViewingSubscriptionDetail && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            className="purchase-tabs"
          >
            <Tab label="Available Content" />
            <Tab label="My Subscriptions" />
            <Tab label="Add-ins" />
          </Tabs>
        )}
      </div>

      {activeTab === 0 && (
        <>
          <SearchFilter
            onSearch={onSearchHandler}
            onFilterClick={handleFilterClick}
            hasActiveFilters={Object.values(selectedFilters).some(
              (filter) => Array.isArray(filter) && filter.length > 0,
            )}
          />

          <PurchaseFilter
            open={filterOpen}
            onClose={handleClose}
            filterOptions={filterOptions}
            onApply={applyFilterHandler}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
          />

          <div className="content-scroll-area">
            <ContentList searchText={searchText} filters={selectedFilters} />
          </div>
        </>
      )}

      {activeTab === 1 && (
        <div className="content-scroll-area">
          <SubscriptionList
            onViewingDetailChange={setIsViewingSubscriptionDetail}
          />
        </div>
      )}

      {activeTab === 2 && (
        <div className="addins-tab-panel">
          <PurchaseAddInsList />
        </div>
      )}
    </div>
  );
};

export default PurchasePage;
